import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import { Play, Pause, Maximize2, Minimize2 } from "lucide-react";

declare global {
  interface Window {
    YT?: {
      Player: new (
        elementId: string,
        options: {
          videoId: string;
          playerVars?: Record<string, number | string>;
          events?: {
            onReady?: (event: { target: YTPlayer }) => void;
            onStateChange?: (event: { data: number; target: YTPlayer }) => void;
          };
        }
      ) => YTPlayer;
      PlayerState: { PLAYING: number; PAUSED: number; ENDED: number; BUFFERING: number };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

interface YTPlayer {
  playVideo: () => void;
  pauseVideo: () => void;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  getPlayerState: () => number;
  destroy: () => void;
}

export interface YouTubeMiniPlayerRef {
  seekTo: (seconds: number) => void;
  playVideo: () => void;
  pauseVideo: () => void;
  getCurrentTime: () => number;
}

interface YouTubeMiniPlayerProps {
  videoId: string;
}

export const YouTubeMiniPlayer = forwardRef<YouTubeMiniPlayerRef, YouTubeMiniPlayerProps>(
  ({ videoId }, ref) => {
    const playerRef = useRef<YTPlayer | null>(null);
    const [isReady, setIsReady] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const rafRef = useRef<number>(0);

    // Cargar API de YouTube
    useEffect(() => {
      const initPlayer = () => {
        if (!window.YT || !videoId) return;
        playerRef.current = new window.YT.Player("youtube-mini-player", {
          videoId,
          playerVars: {
            autoplay: 0,
            controls: 0,
            modestbranding: 1,
            rel: 0,
            playsinline: 1,
          },
          events: {
            onReady: (e) => {
              setIsReady(true);
              setDuration(e.target.getDuration());
            },
            onStateChange: (e) => {
              const ps = window.YT!.PlayerState;
              if (e.data === ps.BUFFERING) return;
              setIsPlaying(e.data === ps.PLAYING);
            },
          },
        });
      };

      if (window.YT && window.YT.Player) {
        initPlayer();
      } else {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScript = document.getElementsByTagName("script")[0];
        firstScript.parentNode?.insertBefore(tag, firstScript);
        window.onYouTubeIframeAPIReady = () => {
          initPlayer();
        };
      }

      return () => {
        cancelAnimationFrame(rafRef.current);
        playerRef.current?.destroy();
        playerRef.current = null;
        window.onYouTubeIframeAPIReady = undefined;
      };
    }, [videoId]);

    // Loop de tiempo actual
    useEffect(() => {
      if (!isPlaying) {
        cancelAnimationFrame(rafRef.current);
        return;
      }
      const update = () => {
        if (playerRef.current) {
          setCurrentTime(playerRef.current.getCurrentTime());
        }
        rafRef.current = requestAnimationFrame(update);
      };
      rafRef.current = requestAnimationFrame(update);
      return () => cancelAnimationFrame(rafRef.current);
    }, [isPlaying]);

    useImperativeHandle(ref, () => ({
      seekTo: (seconds: number) => {
        playerRef.current?.seekTo(seconds, true);
        setCurrentTime(seconds);
      },
      playVideo: () => playerRef.current?.playVideo(),
      pauseVideo: () => playerRef.current?.pauseVideo(),
      getCurrentTime: () => playerRef.current?.getCurrentTime() ?? 0,
    }));

    const togglePlay = useCallback(() => {
      if (!playerRef.current) return;
      if (isPlaying) {
        playerRef.current.pauseVideo();
      } else {
        playerRef.current.playVideo();
      }
    }, [isPlaying]);

    const formatTime = (s: number) => {
      const m = Math.floor(s / 60);
      const sec = Math.floor(s % 60);
      return `${m}:${String(sec).padStart(2, "0")}`;
    };

    const width = isExpanded ? 320 : 200;
    const height = isExpanded ? 180 : 112;

    return (
      <div
        className="rounded-xl overflow-hidden bg-black shadow-2xl border border-[#E8E0D4]/30"
        style={{ width, height: height + 28, transition: "width 0.2s, height 0.2s" }}
      >
        <div className="relative" style={{ width, height }}>
          <div id="youtube-mini-player" className="w-full h-full" />
          {!isReady && (
            <div className="absolute inset-0 flex items-center justify-center text-white text-[10px]">
              Cargando...
            </div>
          )}
        </div>
        <div className="flex items-center justify-between px-2 py-1 bg-[#2C2825]">
          <button
            onClick={togglePlay}
            className="text-white/80 hover:text-white transition-colors"
          >
            {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          </button>
          <span className="text-[9px] text-white/60 font-mono">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
          <button
            onClick={() => setIsExpanded((v) => !v)}
            className="text-white/60 hover:text-white transition-colors"
          >
            {isExpanded ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
          </button>
        </div>
      </div>
    );
  }
);

YouTubeMiniPlayer.displayName = "YouTubeMiniPlayer";
